import { useEffect, useState } from 'react';
import { TrendingDown } from 'lucide-react';
import { api } from '../api';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import BotonExportar from '../ui/BotonExportar';
import { SelectFiltro } from '../ui/Filtros';
import { Cargando } from '../ui/EstadoDePantalla';

import { aCosto, hayCosto, numeroDeCosto } from '../lib/costo';
const pesos = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });
const PERIODOS = [
  { valor: '30', etiqueta: 'Últimos 30 días' },
  { valor: '90', etiqueta: 'Últimos 90 días' },
  { valor: '180', etiqueta: 'Últimos 6 meses' },
  { valor: '365', etiqueta: 'Último año' },
];

// Lo que los conteos han descubierto que falta, acumulado. Tres cortes,
// porque son tres preguntas distintas: QUÉ se pierde, DÓNDE se pierde y
// con quién aparece el patrón.
export default function Mermas({ irA }) {
  const [dias, setDias] = useState('90');
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vigente = true;
    api.get(`/conteos/reportes/mermas?dias=${dias}`)
      .then((d) => vigente && setDatos(d))
      .catch((e) => vigente && setError(e.message));
    return () => { vigente = false; };
  }, [dias]);

  if (error) {
    return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>;
  }
  if (!datos) return <Cargando filas={4} />;

  const vacio = datos.por_producto.length === 0;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Reporte de mermas</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Lo que los conteos físicos han descubierto que falta, en unidades y en dinero.
          </p>
        </div>
        <SelectFiltro valor={dias} onChange={setDias} opciones={PERIODOS} />
      </div>

      <NotaExplicativa titulo="De dónde salen estos números">
        Cuentan las <strong>salidas por merma</strong>: mermas registradas y ajustes a la baja
        (mercancía que se dañó, se perdió o se descontó al cuadrar). No cuentan las liberaciones de
        cuarentena, que es mercancía que <strong>entró</strong>. Es la misma cifra que el tablero: los dos
        números dicen lo mismo. Ordena por <strong>ubicación</strong>: una pérdida repartida
        por todo el almacén y una concentrada en un solo pasillo no tienen la misma causa.
      </NotaExplicativa>

      {vacio ? (
        <EstadoVacio
          icono={TrendingDown}
          titulo="Ninguna merma detectada en este periodo"
          descripcion="O el inventario cuadra, o no hay ningún conteo cerrado. Empieza por un pasillo de alto valor."
        />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Tarjeta titulo="Valor perdido" valor={aCosto(pesos, datos.valor_total)} rojo />
            <Tarjeta titulo="Unidades" valor={Number(datos.unidades_totales)} />
            <Tarjeta titulo="Productos afectados" valor={datos.por_producto.length} />
          </div>

          <Bloque
            titulo="Qué se pierde"
            ayuda={hayCosto(datos.valor_total)
              ? 'Los productos que más valor han perdido en el periodo.'
              : 'Los productos con más unidades perdidas en el periodo.'}
            nombre={`mermas-producto-${dias}d`}
            columnas={[
              { etiqueta: 'SKU', valor: (p) => p.sku },
              { etiqueta: 'Producto', valor: (p) => p.producto },
              { etiqueta: 'Unidades', valor: (p) => Number(p.unidades) },
              { etiqueta: 'Valor', tipo: 'moneda', valor: (p) => numeroDeCosto(p.valor) },
              { etiqueta: 'Veces', valor: (p) => p.veces },
            ]}
            filas={datos.por_producto}
            render={(p) => (
              <tr key={p.sku} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2">
                  <span className="block font-medium text-gray-900">{p.producto}</span>
                  <span className="block text-xs font-mono text-gray-500">{p.sku}</span>
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-700">{Number(p.unidades)}</td>
                <td className="px-4 py-2 text-right tabular-nums font-semibold text-red-700">
                  {aCosto(pesos, p.valor)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-500">{p.veces}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => irA?.('productos', { sku: p.sku })}
                          className="text-sm font-semibold text-[var(--color-marca)]">Kardex</button>
                </td>
              </tr>
            )}
            encabezados={['Producto', 'Unidades', 'Valor', 'Veces', '']}
          />

          <Bloque
            titulo="Dónde se pierde"
            ayuda="Una pérdida repartida por todo el almacén es desorden; concentrada en un pasillo, es otra cosa."
            nombre={`mermas-ubicacion-${dias}d`}
            columnas={[
              { etiqueta: 'Ubicación', valor: (u) => u.ubicacion },
              { etiqueta: 'Nombre', valor: (u) => u.nombre },
              { etiqueta: 'Unidades', valor: (u) => Number(u.unidades) },
              { etiqueta: 'Valor', tipo: 'moneda', valor: (u) => numeroDeCosto(u.valor) },
              { etiqueta: 'Veces', valor: (u) => u.veces },
            ]}
            filas={datos.por_ubicacion}
            render={(u) => (
              <tr key={u.ubicacion} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2">
                  <span className="block font-medium text-gray-900">{u.ubicacion}</span>
                  <span className="block text-xs text-gray-500">{u.nombre}</span>
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-700">{Number(u.unidades)}</td>
                <td className="px-4 py-2 text-right tabular-nums font-semibold text-red-700">
                  {aCosto(pesos, u.valor)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-500">{u.veces}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => irA?.('existencias', { ubicacion: u.ubicacion })}
                          className="text-sm font-semibold text-[var(--color-marca)]">Ver existencias</button>
                </td>
              </tr>
            )}
            encabezados={['Ubicación', 'Unidades', 'Valor', 'Veces', '']}
          />

          {datos.por_responsable.length > 0 && (
            <Bloque
              titulo="Exactitud por responsable"
              ayuda="No es para señalar a nadie: un patrón concentrado en una persona es un dato que hay que ver, sea por descuido o por otra cosa."
              nombre={`exactitud-responsable-${dias}d`}
              columnas={[
                { etiqueta: 'Responsable', valor: (r) => r.responsable },
                { etiqueta: 'Renglones contados', valor: (r) => r.renglones },
                { etiqueta: 'Cuadraron', valor: (r) => r.coinciden },
                { etiqueta: 'Exactitud %', valor: (r) => r.exactitud },
                { etiqueta: 'Unidades desviadas', valor: (r) => Number(r.unidades_desviadas ?? 0) },
              ]}
              filas={datos.por_responsable}
              render={(r) => (
                <tr key={r.responsable} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-900">{r.responsable}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700">{r.renglones}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700">{r.coinciden}</td>
                  <td className={`px-4 py-2 text-right tabular-nums font-semibold ${
                    r.exactitud >= 99 ? 'text-green-700' : r.exactitud >= 95 ? 'text-amber-700' : 'text-red-700'
                  }`}>
                    {r.exactitud}%
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                    {Number(r.unidades_desviadas ?? 0)}
                  </td>
                </tr>
              )}
              encabezados={['Responsable', 'Contados', 'Cuadraron', 'Exactitud', 'Desviadas']}
            />
          )}
        </>
      )}
    </div>
  );
}

function Tarjeta({ titulo, valor, rojo }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3">
      <p className="text-[11px] font-semibold text-gray-500 uppercase">{titulo}</p>
      <p className={`text-2xl font-semibold mt-0.5 ${rojo ? 'text-red-700' : 'text-gray-900'}`}>{valor}</p>
    </div>
  );
}

function Bloque({ titulo, ayuda, nombre, columnas, filas, render, encabezados }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">{titulo}</p>
          <p className="text-xs text-gray-500 mt-0.5">{ayuda}</p>
        </div>
        <BotonExportar nombre={nombre} columnas={columnas} filas={filas} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-semibold text-gray-500 uppercase border-b border-gray-200">
              {encabezados.map((h, i) => (
                <th key={i} className={`px-4 py-2 ${i > 0 && i < encabezados.length - 1 ? 'text-right' : ''}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{filas.map(render)}</tbody>
        </table>
      </div>
    </div>
  );
}
